import { Affix, Anchor, Button, Card, Empty, Layout, message, Spin, Tree } from 'antd'
import { useHistory, useLocation, useParams } from 'react-router'
import { useCallback, useEffect, useState } from 'react'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import NavHeader from '../../../../components/NavHeader'
import AxiosUtil from '../../../../utils/AxiosUtil'
import { toMultiDimensionArr } from '../../../../utils/DataUtil'
import styles from './helpView.module.less'
import type { DataNode } from 'antd/es/tree'
import NavFooter from '../../../../components/NavFooter'
import Prism from 'prismjs'
import 'prismjs/themes/prism.min.css'

type IAnchorItem = { id: string; tagName: string; title: string; parentId: string; children?: IAnchorItem[] }
type ICatalogItem = {
  functionId: number
  functionName: string
  functionType: 2
  announcementId: number
  parentId?: number
  sortIndex?: null
  announcementcontentid?: number
  flag: 1 | 0
  children?: []
}

const defaultHtml = `<h1>123</h1><p>12345678987654321234567898765432123456789</p><h2>123</h2><p>123</p><h3>123</h3><p>123</p><h2>123</h2><p>123</p>`
const editorContentViewBoxId = 'editor-content-view' // 文档查看容器的 id
const headIdPrefix = 'editor-head-' // 大纲 id 的前缀
/* 渲染右侧大纲的函数 */
const renderHeader = (arr: IAnchorItem[]): any => {
  return arr.map((item) => {
    if (item.children?.length)
      return (
        <Anchor.Link key={item.id} href={'#' + item.id} title={item.title}>
          {renderHeader(item.children)}
        </Anchor.Link>
      )
    else return <Anchor.Link key={item.id} href={'#' + item.id} title={item.title} />
  })
}
/* Tree 组件用到的数据 */
const toTreeCatalogs = (catalogs: ICatalogItem[]): DataNode[] => {
  return catalogs.map((item) => {
    if (item.children?.length) return { title: item.functionName, key: item.functionId, other: item, children: toTreeCatalogs(item.children) }
    else return { title: item.functionName, key: item.functionId, other: item }
  })
}

export default function HelpView() {
  const history = useHistory()
  const params = useParams<{ id: string }>() // 路由传递的参数
  const location: { state: ICatalogItem } = useLocation()
  const [html, setHtml] = useState(defaultHtml) // 富文本生成的 html
  const [headers, setHeaders] = useState<any[]>([]) // 右侧大纲项
  const [catalogs, setCatalogs] = useState<DataNode[]>([]) // 存储的目录信息
  const [curCatalog, setCurCatalog] = useState({} as ICatalogItem)
  const [loading, setLoading] = useState(false)

  /* 加载目录的函数 */
  const loadCatalog = useCallback(
    (isDel = false) => {
      AxiosUtil.AxiosRequest('/api/announcement/getAnnfunctionList', { announcementId: +params.id }, (suc, dat: ICatalogItem[]) => {
        if (suc) {
          const cur = isDel ? dat[0] : Object.keys(location.state || {}).length ? location.state : dat[0] ? dat[0] : ({} as ICatalogItem)
          setCurCatalog(cur)
          setCatalogs(toTreeCatalogs(dat))
        }
      })
    },
    [location.state, params.id]
  )
  /* 点击添加目录 */
  const addCatalog = () => history.push({ pathname: '/admin/help/' + params.id + '/edit', state: {} })
  /* 点击目录项 */
  const clickCatalog = (selectKeys: any, { node }: any) => selectKeys?.length && selectKeys[0] !== curCatalog.functionId && setCurCatalog(node.other)
  /* 点击编辑目录 */
  const clickEdit = () => history.push({ pathname: '/admin/help/' + params.id + '/edit', state: curCatalog })
  /* 点击删除目录 */
  const clickDel = () => {
    AxiosUtil.AxiosRequest('/api/announcement/editAnnfunction', { list: [{ ...curCatalog, flag: 0 }] }, (suc) => {
      if (suc) {
        message.success('删除成功 !')
        setCurCatalog({} as ICatalogItem)
        loadCatalog(true)
      } else message.error('删除失败 !')
    })
  }
  /* 点击添加目录 */
  const clickAdd = () => history.push({ pathname: '/admin/help/' + params.id + '/edit/' + curCatalog.functionId, state: curCatalog })
  /* 页面加载完成后, 加载目录列表 */
  useEffect(() => loadCatalog(), [loadCatalog])
  /* 根据富文本内容生成目录 */
  useEffect(() => {
    const view = document.getElementById(editorContentViewBoxId)
    if (view) {
      const tocTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6']
      const tocArr: IAnchorItem[] = []
      const headDoms = Array.from(view.children).filter((item) => tocTags.includes(item.tagName))
      headDoms.forEach((item: any, idx, arr) => {
        item.id = headIdPrefix + (idx + 1) // 添加 id
        const curTagIdx = tocTags.findIndex((i) => i === item.tagName) // 当前 tag 层级 (h1 是最高级, h6 是最低级)
        if (curTagIdx === 0) tocArr.push({ id: item.id, tagName: item.tagName, title: item.innerText, parentId: '' })
        else if (curTagIdx !== -1) {
          const prevs = arr.filter((i, index1) => index1 < idx && curTagIdx > tocTags.findIndex((i1) => i1 === i.tagName))
          const parentId = prevs[prevs.length - 1]?.id
          tocArr.push({ id: item.id, tagName: item.tagName, title: item.innerText, parentId })
        }
      })
      setHeaders(toMultiDimensionArr(tocArr, 'id', 'parentId'))
      Prism.highlightAll()
    }
  }, [html])
  /* 获取当前目录对应的内容 */
  useEffect(() => {
    if (curCatalog.announcementcontentid) {
      setLoading(true)
      AxiosUtil.AxiosRequest('/api/announcement/getAnnContentInfo', { id: curCatalog.announcementcontentid }, (suc, dat) => {
        if (suc) setHtml(dat.summary)
        setLoading(false)
      })
    } else setHtml('')
  }, [curCatalog.announcementcontentid])

  return (
    <Layout>
      <NavHeader />
      <Spin spinning={loading}>
        <div id={styles.view}>
          <Affix className={'catalog'} offsetTop={10}>
            <div className={'catalog-title'}>
              目录
              <Button size={'small'} icon={<PlusOutlined />} onClick={addCatalog} />
            </div>
            <div className={'operate'}>
              <Button disabled={!curCatalog.functionId} icon={<DeleteOutlined />} size={'small'} onClick={clickDel}>
                删除
              </Button>
              <Button disabled={!curCatalog.functionId} icon={<EditOutlined />} size={'small'} onClick={clickEdit}>
                编辑
              </Button>
              <Button disabled={!curCatalog.functionId} icon={<PlusOutlined />} size={'small'} onClick={clickAdd}>
                新增
              </Button>
            </div>
            <Tree key={catalogs.length} defaultExpandAll treeData={catalogs} selectedKeys={[curCatalog.functionId]} onSelect={clickCatalog} />
          </Affix>
          <div className={'content'}>
            <div className={'view-content'}>
              <Card size={'small'}>
                <div id={editorContentViewBoxId} className="editor-content-view" dangerouslySetInnerHTML={{ __html: html }} />
                {html ? '' : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                <NavFooter />
              </Card>
            </div>
          </div>
          <Affix offsetTop={10} className={'header'}>
            <div className={'header-title'}>大纲</div>
            <Anchor onClick={(e) => e.preventDefault()}>{renderHeader(headers)}</Anchor>
          </Affix>
        </div>
      </Spin>
    </Layout>
  )
}
